import { Input, Form, Space, Button } from "antd";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { useCallback } from "react";
import Select from "../../../components/generic/Select";
import { QUESTION_TYPES } from "../../../constant";

const TopicQuestions = ({ form, isWorkFlow = false }) => {
  // Watch form values efficiently
  const questions = Form.useWatch("questions", form) || [];

  const reorderQuestions = useCallback((questionsList) => {
    return questionsList.map((q, index) => ({
      ...q,
      order: index + 1,
    }));
  }, []);

  // Memoized callbacks for better performance
  const handleAddQuestion = useCallback((add) => {
    const currentQuestions = form.getFieldValue("questions") || [];
    add({
      id: `${Date.now()}-${Math.random()}`,
      question: "",
      type: "string",
      options: [],
      order: currentQuestions.length + 1,
    });
  }, []);

  const handleRemoveQuestion = useCallback((remove, name) => {
    remove(name);
    setTimeout(() => {
      const currentQuestions = form.getFieldValue("questions") || [];
      const reordered = reorderQuestions(currentQuestions);
      form.setFieldsValue({ questions: reordered });
    }, 0);
  }, []);

  const handleRemoveOption = useCallback((remove, name) => {
    remove(name);
  }, []);

  const handleAddOption = useCallback((add) => {
    add("");
  }, []);

  // Render dropdown options section
  const renderDropdownOptions = useCallback(
    (field, questionIndex) => {
      const currentQuestion = questions[questionIndex];

      if (currentQuestion?.type !== "dropdown") {
        return null;
      }

      if (!currentQuestion || currentQuestion.length === 0) {
        const updatedQuestions = [...questions];
        updatedQuestions[field.name] = {
          ...updatedQuestions[field.name],
          options: [""],
        };
        form.setFieldsValue({ questions: updatedQuestions });
      }

      return (
        <Form.List name={[field.name, "options"]}>
          {(optionFields, { add: addOption, remove: removeOption }) => (
            <div className="options-section">
              <Form.Item
                label="Options"
                style={{ height: currentQuestion.length === 0 ? 0 : "auto" }}
              >
                {optionFields.map((optionField, optionIndex) => (
                  <Space key={optionField.key} className="option-item">
                    <Form.Item
                      {...optionField}
                      name={optionField.name}
                      rules={[
                        {
                          required: true,
                          message: "Please enter option",
                        },
                        {
                          whitespace: true,
                          message: "Option cannot be empty",
                        },
                      ]}
                      style={{ margin: 0, flex: 10 }}
                    >
                      <Input
                        placeholder={`Option ${optionIndex + 1}`}
                        style={{ width: "300px" }}
                      />
                    </Form.Item>
                    {optionFields.length > 1 && (
                      <Button
                        icon={<MinusCircleOutlined />}
                        onClick={() =>
                          handleRemoveOption(removeOption, optionField.name)
                        }
                        type="text"
                        danger
                      />
                    )}
                  </Space>
                ))}
              </Form.Item>
              <Button
                type="dashed"
                onClick={() => handleAddOption(addOption)}
                icon={<PlusOutlined />}
                style={{ marginBottom: "16px" }}
              >
                Add Option
              </Button>
            </div>
          )}
        </Form.List>
      );
    },
    [questions, handleAddOption, handleRemoveOption],
  );

  // Render individual question card
  const renderQuestionCard = useCallback(
    ({ key, ...field }, index, fields, remove) => (
      <div key={key} className="question-card">
        <div
          className="flex justify-space-between align-center question-header"
          style={{ display: isWorkFlow ? "none" : "flex" }}
        >
          <span className="question-number">Question Number : {index + 1}</span>
          {fields.length > 1 && (
            <MinusCircleOutlined
              className="remove-btn"
              onClick={() => handleRemoveQuestion(remove, field.name)}
              aria-label={`Remove question ${index + 1}`}
            />
          )}
        </div>

        {/* Hidden ID field */}
        <Form.Item
          {...field}
          name={[field.name, "id"]}
          initialValue={`${Date.now()}-${index}`}
          hidden
        >
          <Input />
        </Form.Item>

        <Form.Item name={[field.name, "order"]} hidden>
          <Input />
        </Form.Item>

        {/* Question type */}
        <Form.Item
          {...field}
          label="Type"
          name={[field.name, "type"]}
          style={{ width: "200px" }}
          rules={[{ required: true, message: "Please select type" }]}
        >
          <Select placeholder="Select question type" options={QUESTION_TYPES} />
        </Form.Item>

        {/* Question text */}
        <Form.Item
          {...field}
          label="Question"
          name={[field.name, "question"]}
          rules={[
            { required: true, message: "Please enter question" },
            { whitespace: true, message: "Question cannot be empty" },
            { max: 500, message: "Question cannot exceed 500 characters" },
          ]}
        >
          <Input.TextArea
            rows={2}
            placeholder="Enter your question"
            showCount
            maxLength={500}
          />
        </Form.Item>

        {/* Dropdown options */}
        {renderDropdownOptions(field, index)}
      </div>
    ),
    [handleRemoveQuestion, renderDropdownOptions],
  );

  return (
    <Form.List
      name="questions"
      rules={[
        {
          validator: async (_, questions) => {
            if (!questions || questions.length === 0) {
              return Promise.reject(
                new Error("At least one question is required"),
              );
            }
          },
        },
      ]}
    >
      {(fields, { add, remove }, { errors }) => (
        <>
          <div className="questions-container">
            {fields.map((field, index) =>
              renderQuestionCard(field, index, fields, remove),
            )}
          </div>
          {!isWorkFlow && (
            <Button
              type="dashed"
              onClick={() => handleAddQuestion(add)}
              icon={<PlusOutlined />}
              className="add-question-btn"
            >
              Add Question
            </Button>
          )}

          <Form.ErrorList errors={errors} />
        </>
      )}
    </Form.List>
  );
};

export default TopicQuestions;
